import { useMutation } from '@apollo/client'
import {
  CreateInquiryByMemberDocument,
  CreateInquiryByMemberMutationVariables,
  CreateInquiryByNonMemberDocument,
  GetInquiriesByOffsetDocument,
  InquirySortBy,
} from '@lib/graphql/documents.generated'
import { useMe } from '@modules/users/hooks/use-me'
import compact from 'lodash/compact'
import { INQUIRIES_PAGE_LIMIT } from 'pages/settings/inquiries'
import { InquiryForm } from 'pages/support/inquiry/write'
import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'

export const useCreateInquiry = () => {
  const { me } = useMe()
  const isLoggendIn = Boolean(me)

  // Form Context
  const { handleSubmit, setError } = useFormContext<InquiryForm>()

  const [createInquiryByMember] = useMutation(CreateInquiryByMemberDocument, {
    refetchQueries: [
      {
        query: GetInquiriesByOffsetDocument,
        variables: {
          offset: 0,
          limit: INQUIRIES_PAGE_LIMIT,
          sortBy: InquirySortBy.CreatedAtDesc,
          filter: {
            questionerId: me?.id,
          },
        },
      },
    ],
  })
  const [createInquiryByNonMember] = useMutation(
    CreateInquiryByNonMemberDocument
  )

  const onCreateInquiry = useCallback(
    (
      input: CreateInquiryByMemberMutationVariables['input'],
      done?: (id?: string) => void
    ) => {
      if (isLoggendIn) {
        createInquiryByMember({
          variables: {
            input,
          },
          onCompleted: (data) => {
            done?.(data.createInquiryByMember.id)
          },
        })
      } else {
        createInquiryByNonMember({
          variables: {
            input,
          },
          onCompleted: () => {
            done?.()
          },
        })
      }
    },
    [createInquiryByMember, createInquiryByNonMember, isLoggendIn]
  )

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)

  const onSubmit = useCallback(
    (done?: (id?: string) => void) =>
      handleSubmit(async ({ question, email, title, files }) => {
        setSubmitError(null)
        setSubmitLoading(true)

        if (files && files?.length > 5) {
          setError('files', {
            message: '파일은 최대 5개까지 업로드할 수 있습니다',
          })
        }
        try {
          onCreateInquiry(
            {
              question,
              email,
              title,
              files: compact(files),
            },
            done
          )
        } catch (error) {
          console.error(error)
          return setSubmitError('문의할 수 없습니다. 고객센터에 문의해주세요.')
        } finally {
          setSubmitLoading(false)
        }
      }),
    [handleSubmit, onCreateInquiry, setError]
  )

  const onSubmitError = useCallback((err: string | null) => {
    setSubmitError(err)
  }, [])

  return { onSubmit, submitError, submitLoading, onSubmitError }
}
